import { useState } from 'react';
import type { ExamScore } from '@/types';
import { buildExamComparison, buildExamTitle, groupExamScores } from '@/lib/scores';

interface ParentExamScoreSectionProps {
  scores: ExamScore[];
}

const typeToneClass: Record<string, string> = {
  중간고사: 'bg-primary-100 text-primary-700',
  기말고사: 'bg-secondary-100 text-secondary-900',
  모의고사: 'bg-accent-100 text-accent-800',
};

function ScoreMetric({ label, value }: { label: string; value?: string }) {
  return (
    <div className="rounded-md bg-background-100 px-2.5 py-1.5">
      <p className="text-[11px] text-foreground-500">{label}</p>
      <p className="text-sm font-semibold text-foreground-950">{value || '-'}</p>
    </div>
  );
}

function DeltaBadge({ delta }: { delta: number | null }) {
  if (delta === null) {
    return (
      <span className="rounded-full bg-background-200 px-2 py-0.5 text-[11px] font-semibold text-foreground-500">
        비교 불가
      </span>
    );
  }
  if (delta === 0) {
    return (
      <span className="flex items-center gap-0.5 rounded-full bg-background-200 px-2 py-0.5 text-[11px] font-semibold text-foreground-600">
        <i className="ri-subtract-line"></i>
        유지
      </span>
    );
  }
  const up = delta > 0;
  return (
    <span
      className={`flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
        up ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-800'
      }`}
    >
      <i className={up ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}></i>
      {up ? '+' : ''}
      {delta}
    </span>
  );
}

export default function ParentExamScoreSection({ scores }: ParentExamScoreSectionProps) {
  const sessions = groupExamScores(scores);
  const [openKey, setOpenKey] = useState<string | null>(sessions[0]?.key ?? null);

  return (
    <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <h2 className="flex flex-wrap items-center gap-2 text-sm font-bold text-foreground-950">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
          <i className="ri-bar-chart-box-line"></i>
        </span>
        과목별 시험 성적
        <span className="rounded-full bg-background-200 px-2.5 py-0.5 text-[11px] font-semibold text-foreground-600">
          {sessions.length}회
        </span>
      </h2>

      {sessions.length === 0 ? (
        <p className="mt-4 rounded-lg border border-dashed border-background-300 py-8 text-center text-sm text-foreground-500">
          아직 기록된 시험 성적이 없어요.
        </p>
      ) : (
        <div className="mt-4 flex flex-col gap-3">
          {sessions.map((session, idx) => {
            const prev = sessions[idx + 1] ?? null;
            const comparison = prev ? buildExamComparison(session.rows, prev.rows) : [];
            const isOpen = openKey === session.key;
            const title = buildExamTitle(session);
            return (
              <div
                key={session.key}
                className="overflow-hidden rounded-lg border border-background-200 bg-background-100"
              >
                <button
                  type="button"
                  onClick={() => setOpenKey(isOpen ? null : session.key)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                >
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                        typeToneClass[session.examType] ?? 'bg-background-200 text-foreground-700'
                      }`}
                    >
                      {session.examType}
                    </span>
                    <span className="text-sm font-semibold text-foreground-950">{title}</span>
                    <span className="rounded-full bg-background-200 px-2.5 py-0.5 text-[11px] font-semibold text-foreground-600">
                      {session.rows.length}과목
                    </span>
                  </div>
                  <i
                    className={`text-lg text-foreground-400 ${
                      isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
                    }`}
                  ></i>
                </button>

                {isOpen && (
                  <div className="border-t border-background-200 bg-background-50 p-4">
                    <div className="flex flex-col gap-2">
                      {session.rows.map((r) => (
                        <div
                          key={r.id}
                          className="rounded-lg border border-background-200 bg-background-100 p-3"
                        >
                          <p className="text-sm font-semibold text-foreground-950">
                            {r.subject || '과목 미입력'}
                          </p>
                          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                            <ScoreMetric label="원점수" value={r.score} />
                            <ScoreMetric label="표준점수" value={r.standard_score} />
                            <ScoreMetric label="백분위" value={r.percentile} />
                            <ScoreMetric label="등급 / 석차" value={r.rank_grade} />
                          </div>
                        </div>
                      ))}
                    </div>

                    {prev && comparison.length > 0 && (
                      <div className="mt-4 rounded-lg border border-secondary-200 bg-secondary-50 p-3">
                        <p className="flex flex-wrap items-center gap-1.5 text-xs font-bold text-secondary-900">
                          <i className="ri-arrow-left-right-line"></i>
                          이전 시험과 비교
                          <span className="font-medium text-secondary-800">
                            ({buildExamTitle(prev)})
                          </span>
                        </p>
                        <div className="mt-2 flex flex-col gap-2">
                          {comparison.map((row) => (
                            <div
                              key={row.subject}
                              className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-background-50 px-3 py-2"
                            >
                              <span className="text-sm font-semibold text-foreground-950">
                                {row.subject}
                              </span>
                              <div className="flex items-center gap-2 text-xs">
                                <span className="text-foreground-500">{row.prevScore}</span>
                                <i className="ri-arrow-right-line text-foreground-400"></i>
                                <span className="font-semibold text-foreground-950">
                                  {row.currScore}
                                </span>
                                <DeltaBadge delta={row.delta} />
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="mt-2 text-[11px] text-secondary-700">
                          원점수 기준 이전 시험 대비 변화예요.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}